import * as React from 'react';
import { useState } from 'react';
import styled from 'styled-components/macro';
import { PreviewButton } from './components/PreviewButton';
import { BiBaseTab } from './components/BiBaseTab';
import { translations } from '../../../locales/translations';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useContractDataSlice } from './slice';
import {
  selectContractError,
  selectContractInfo,
  selectContractInfoView,
  selectDepositView,
  selectContractNumber,
  selectOneOffView,
  selectOriginalView,
  selectPreviewMode,
  selectRecurringView,
} from './slice/selectors';
import { AcknowledgeDialog } from './components/AcknowledgeDialog';
import { Chatter } from './components/Chatter';
import { AcknowledgeButton } from './components/AcknowledgeButton';
import { biStatus, biType } from '../../../locales/types';
import { Alert, Button, LinearProgress, Stack } from '@mui/material';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import CachedIcon from '@mui/icons-material/Cached';
import {
  selectAccessRight,
  selectContractStatus,
  selectSelectedPreviewSection,
  selectUsername,
} from '../WorkListPage/slice/selectors';
import { useWorkListStateSlice } from '../WorkListPage/slice';
import { RemarkButton } from './components/RemarkButton';
import { RemarkDialog } from './components/RemarkDialog';
import ErrorDialog from '../../components/ErrorDialog';
import { apiList } from '../../../locales/apiList';
import { UseAxios } from '../../../utils/UseAxios';
import BarAndMenu from '../../components/BarAndMenu';
import { Link } from '../../components/Link';
import { ChatterButton } from './components/Chatter/ChatterButton';
import { PopoverFileList } from './components/PopoverFileList';
import { ContractErrorType } from './slice/types';
import { fromPathSegment, toPathSegment } from '../../../utils/contractNumber';

export function ContractDetailPage(props) {
  const { t } = useTranslation();
  const contractNumber: string = fromPathSegment(props.match.params.id);
  const dafaultOpenChatter: string = props.match.params.openChatter;
  let contractDataAction = useContractDataSlice().actions;
  const error = useSelector(selectContractError);
  let worklistAction = useWorkListStateSlice().actions;
  const dispatch = useDispatch();
  const storedContractNumber = useSelector(selectContractNumber);
  const contractInfo = useSelector(selectContractInfo);
  const previewMode = useSelector(selectPreviewMode);
  const contractInfoView = useSelector(selectContractInfoView);
  const recurringView = useSelector(selectRecurringView);
  const oneOffView = useSelector(selectOneOffView);
  const depositView = useSelector(selectDepositView);
  const originalView = useSelector(selectOriginalView);
  const contractStatus = useSelector(selectContractStatus);
  const username = useSelector(selectUsername);
  const accessright = useSelector(selectAccessRight);
  let acknowledgeType: biType[] = [];
  const selectedPreviewSection = useSelector(selectSelectedPreviewSection);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [remarkOpen, setRemarkOpen] = useState(false);
  const [allowAcknowledge, setAllowAcknowledge] = useState(true);
  const [loading, setLoading] = useState(false);
  const [remarkValue, setRemarkValue] = useState('');
  const [reloadAmendment, setReloadAmendment] = useState(false);
  const [resetPreview, setResetPreview] = useState(false);
  const [selectedTab, setSelectedTab] = React.useState(0);
  const [openChatter, setOpenChatter] = React.useState(dafaultOpenChatter === 'openChatter');
  const [quoteContractLoading, setQuoteContractLoading] = useState(false);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [popoverFileList, setPopoverFileList] = useState<[]>([]);
  const popoverOpen = Boolean(popoverAnchorEl);
  const contractInfoIsPending = contractStatus 
    && contractStatus.contractInfoStatus !== biStatus.COMPLETE
    && contractStatus.contractInfoStatus !== biStatus.AUTOComplete
    && originalView && contractInfoView;
  const selectedSectionIsPending = contractStatus && contractStatus[`${selectedPreviewSection}Status`] === biStatus.PENDING;

  React.useEffect(() => {
    dispatch(contractDataAction.setContractNumber(contractNumber));
    if (storedContractNumber) {
      reloadClick();
    }
  }, [contractNumber]);

  React.useEffect(() => {
    if (!accessright.edit && accessright.view) {
      setOpenChatter(true);
    }
  }, [accessright]);

  React.useEffect(() => {
    const selectedSectionCanPreview = previewMode && selectedPreviewSection !== biType.OTHERREIMBURSEMENT;
    // a contract does not necessarily have a recurring section
    if (selectedSectionCanPreview && (selectedSectionIsPending || contractInfoIsPending)) {
      setAllowAcknowledge(true);
    } else {
      setAllowAcknowledge(false);
    }
    if (contractStatus && contractStatus.remark) {
      setRemarkValue(contractStatus.remark);
    }
  }, [selectedPreviewSection, previewMode, contractStatus]);

  React.useEffect(() => {
    if (contractStatus && contractStatus.remark) {
      setRemarkValue(contractStatus.remark);
    }
  }, [contractStatus]);

  React.useEffect(() => {
    if (error) {
      let errorMessage = translations.error.commonError;
      if (error === ContractErrorType.NOT_FOUND) {
        errorMessage = translations.error.notFound;
      } else if (error === ContractErrorType.FORBIDDEN) {
        errorMessage = translations.error.forbidden;
      } else if (error === ContractErrorType.UNAUTHORIZED) {
        errorMessage = translations.error.unauthorized;
      } else if (error === ContractErrorType.NETWORK_ERROR) {
        errorMessage = translations.error.networkError;
      } else if (error === ContractErrorType.SERVER_ERROR) {
        errorMessage = translations.error.serverError;
      }
      dispatch(worklistAction.updateErrorWordings(errorMessage));
      dispatch(worklistAction.updateErrorDialog(true));
    }
  }, [error])

  const handleOpenAcknowledgeDialog = () => {
    setDialogOpen(true);
  };

  function handleCloseAcknowledgeDialog() {
    setDialogOpen(false);
  }

  const handleConfirmAcknowledge = async () => {
    setLoading(true);
    let newView = Object.assign({}, originalView);
    if (contractStatus && contractStatus.contractInfoStatus !== biStatus.COMPLETE && newView && contractInfoView) {
      acknowledgeType.push(biType.CONTRACTINFO);
      newView.contractInfo = contractInfoView;
    }
    if (contractStatus?.terminationStatus === biStatus.PENDING) {
      acknowledgeType.push(biType.TERMINATION);
    }
    if (contractStatus?.changeBillDateStatus === biStatus.PENDING) {
      acknowledgeType.push(biType.CHANGEBILLDATE);
    }
    if (selectedSectionIsPending) {
      if (selectedPreviewSection === biType.RECURRING && newView && recurringView) {
        acknowledgeType.push(biType.RECURRING);
        newView.recurring = recurringView;
      } else if (selectedPreviewSection === biType.ONEOFF && newView && oneOffView) {
        acknowledgeType.push(biType.ONEOFF);
        newView.oneOff = oneOffView;
      } else if (selectedPreviewSection === biType.DEPOSIT && newView && depositView) {
        acknowledgeType.push(biType.DEPOSIT);
        newView.deposit = depositView;
      }
    }
    const body = {
      user: username,
      acknowledgeType: acknowledgeType,
      newView: newView,
    };
    let updateStatusResult = await UseAxios(`${apiList.putWorklistStatusURL}${toPathSegment(contractNumber)}/status`, 'put', body);
    if (!updateStatusResult.error) {
      dispatch(contractDataAction.updateOriginalView(newView));
      dispatch(worklistAction.updateContractStatus(updateStatusResult.data));
    } else {
      dispatch(worklistAction.updateErrorWordings(translations.error.commonError));
      dispatch(worklistAction.updateErrorDialog(true));
    }
    setReloadAmendment(!reloadAmendment);
    setResetPreview(true);
    setLoading(false);
    handleCloseAcknowledgeDialog();
  };

  const handleOpenRemarkDialog = () => {
    setRemarkOpen(true);
  };

  const handleCloseRemarkDialog = () => {
    setRemarkOpen(false);
  };

  const downloadQuote = async (ev) => {
    setQuoteContractLoading(true);
    let serachResult = await UseAxios(`${apiList.getQuoteURL}${toPathSegment(contractNumber)}/quote`, 'get');
    setQuoteContractLoading(false);
    if (serachResult && serachResult.data.length !== 0) {
      if (serachResult.data.length === 1) {
        window.open(serachResult.data[0].url, '_blank');
      } else {
        // open a list UI with links
        setPopoverAnchorEl(ev.target);
        setPopoverFileList(serachResult.data);
      }
      
    } else {
      dispatch(worklistAction.updateErrorWordings(translations.error.missingFile));
      dispatch(worklistAction.updateErrorDialog(true));
    }
  };

  const reloadClick = async () => {
    setLoading(true);
    setResetPreview(true);
    setReloadAmendment(!reloadAmendment);
    let getBIResult = await UseAxios(`${apiList.getBIURL}${toPathSegment(contractNumber)}`, 'get');
    if (!getBIResult.error) {
      dispatch(contractDataAction.reposLoaded(getBIResult.data));
    } else {
      dispatch(worklistAction.updateErrorWordings(translations.error.commonError));
      dispatch(worklistAction.updateErrorDialog(true));
    }
    let updateStatusResult = await UseAxios(`${apiList.getWorklistByIDURL}${toPathSegment(contractNumber)}`, 'get');
    if (updateStatusResult) {
      dispatch(worklistAction.updateContractStatus(updateStatusResult.data));
    } else {
      dispatch(worklistAction.updateErrorWordings(translations.error.commonError));
      dispatch(worklistAction.updateErrorDialog(true));
    }
    setLoading(false);
  };

  const handleConfirmRemark = async () => {
    setLoading(true);
    const body = {
      user: username,
      content: remarkValue,
    };
    let updatedContractStatus = await UseAxios(`${apiList.putWorklistRemarkURL}${toPathSegment(contractNumber)}/remark`, 'put', body);
    if (!updatedContractStatus.error) {
      dispatch(worklistAction.updateContractStatus(updatedContractStatus.data));
    } else {
      dispatch(worklistAction.updateErrorWordings(translations.error.commonError));
      dispatch(worklistAction.updateErrorDialog(true));
    }
    setLoading(false);
    handleCloseRemarkDialog();
  };

  return (
    <Wrapper>
      <BarAndMenu />
      {loading && contractInfo && <LinearProgress />}
      {!contractInfo ?
        <LinearProgress />
        :
        <div className='content-container'>
          <div className='bi-detail-header'>
            <div className={`left-header ${(selectedTab !== 0) && 'left-header-only'}`}>
              <div className='contract-no'>{contractInfo.contractNumber}</div>
              <Button className='quote-btn' variant='contained' startIcon={<RequestQuoteIcon />}
                      onClick={downloadQuote}>
                {
                  quoteContractLoading ?
                    t(translations.loading) :
                    (contractNumber.startsWith("OA-CL") ?
                      t(translations.contractDetail.contractInformation.quoteOrContract) :
                      t(translations.contractDetail.contractInformation.preagreement)
                    )
                }
              </Button>
              <PopoverFileList open={popoverOpen} anchorEl={popoverAnchorEl} fileList={popoverFileList} onClose={()=>{setPopoverAnchorEl(null);}} />
              {(contractStatus?.biStatus === biStatus.RENEWEDOP && contractInfo.renewedByContract) &&
              <Stack className='alert-warning' spacing={2}>
                <Alert severity='warning' variant='outlined'>{t(translations.popUp.renewedOpenContract)}
                  <Link className='void-hyper'
                        to={`${process.env.PUBLIC_URL}/detail/${toPathSegment(contractInfo.renewedByContract)}`}>
                    {contractInfo.renewedByContract}
                  </Link>
                </Alert>
              </Stack>
              }
              {(contractStatus && contractStatus.biStatus === biStatus.RENEWEDOPReminder) &&
              <Stack className='alert-warning' spacing={2}>
                <Alert severity='info' variant='outlined'>{t(translations.popUp.previousOpenContractReminder)}
                </Alert>
              </Stack>
              }
              {(contractStatus && contractStatus.biStatus === biStatus.VOIDED) &&
              <Stack className='alert-warning' spacing={2}>
                <Alert severity='error' variant='outlined'>{t(translations.popUp.voidedContract)}
                </Alert>
              </Stack>
              }
              {accessright.edit &&
              <div className='right-align'>
                <RemarkButton openDialog={handleOpenRemarkDialog} haveIcon={remarkValue} />
              </div>
              }
            </div>
            <div className='right-header' hidden={selectedTab !== 0}>
              {accessright.edit &&
              <span>
                  <AcknowledgeButton className='ack-btn' openDialog={handleOpenAcknowledgeDialog}
                                     readOnly={!allowAcknowledge} />
                  <PreviewButton className='ack-btn' resetPreview={resetPreview} setResetPreview={setResetPreview} />
                </span>
              }
              <Button className='reload-btn' onClick={reloadClick}><CachedIcon /></Button>
            </div>
          </div>
          <div className='bi-amendment'>
            <BiBaseTab {...props} reloadClick={reloadClick} reloadAmendment={reloadAmendment} setSelectedTab={setSelectedTab} />
          </div>
          <ChatterButton openChatter={openChatter} setOpenChatter={setOpenChatter} />
          <Chatter openChatter={openChatter} />
        </div>
      }
      <AcknowledgeDialog open={dialogOpen} closeDialog={handleCloseAcknowledgeDialog}
                         confirmAndCloseDialog={handleConfirmAcknowledge}
                         selectedPreviewSection={selectedPreviewSection}
                         contractInfoIsPending={contractInfoIsPending}
                         selectedSectionIsPending={selectedSectionIsPending} />

      {contractStatus &&
      <RemarkDialog remarkValue={remarkValue} setRemarkValue={setRemarkValue} setLoading={setLoading}
                    open={remarkOpen} closeDialog={handleCloseRemarkDialog}
                    confirmAndCloseDialog={handleConfirmRemark} />
      }
      <ErrorDialog />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  font-size: 14px;
  height: 100%;
  padding-left: 72px;

  .content-container {
    padding-bottom: 10px;
  }

  .bi-detail-header {
    display: flex;
    height: 50px;
    
    .left-header-only {
      width: 100% !important;
      padding-right: 5px;
    }
    
    .left-header {
      display: flex;
      align-items: center;
      width: 70%;
      margin-right: 10px;

      .right-align {
        margin-left: auto;
        margin-right: 3px;
      }

      .contract-no {
        padding-left: 15px;
        font-size: 20px;
        font-weight: 500;
      }

      .create-date-container {
        display: flex;
        align-items: center;
        margin-left: auto;
        margin-right: 0;
      }

      .alert-warning {
        margin: 0 auto;

        .void-hyper {
          color: rgb(102, 60, 0);
          text-decoration: underline;
        }

        .MuiAlert-outlined {
          padding: 0 10px;
          margin: 0 auto;
        }
      }

      .quote-btn {
        display: flex;
        align-items: center;
        float: right;
        margin-left: 10px;
        color: ${p => p.theme.buttonFont};
        background: ${p => p.theme.buttonColor};
        width: 200px;
        font-size: 12px;
        padding: 5px;
        height: 30px;

        .MuiSvgIcon-root {
          font-size: 18px;
        }
      }
    }

    .right-header {
      width: 30%;
      align-self: center;
      float: right;

      .reload-btn {
        color: ${p => p.theme.buttonFont};
        background: ${p => p.theme.buttonColor};
        float: right;
        margin-right: 10px;
        height: 30px;
      }
    }
  }

  .bi-amendment {
    display: flex;
  }
`;
